import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Divider, Box, Chip, IconButton, CircularProgress } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { toast } from 'react-toastify';
import { formatToDDMMYYYY } from '../utils/Validation';
import { verifyPaymentStatus } from '../services/apiService';

interface BankTransaction {
  id: number;
  paymentRefNo: string;
  transactionId: string;
  amount: string;
  transferType: string;
  beneAccName: string;
  beneAccNo: string;
  beneIfsc: string;
  status: string;
  createdAt: string;
  finalResponse: any;
}

interface TransactionDetailsModalProps {
  open: boolean;
  onClose: () => void;
  transaction: BankTransaction | null;
  onRefresh?: () => void | Promise<void>;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({ open, onClose, transaction, onRefresh }) => {
  const [refreshing, setRefreshing] = useState(false);

  if (!transaction) return null;

  const utrNo = transaction.finalResponse?.UTR_NO || 
                (transaction.finalResponse?.ALL_RECORDS && 
                 Array.isArray(transaction.finalResponse.ALL_RECORDS) && 
                 transaction.finalResponse.ALL_RECORDS.length > 0 && 
                 transaction.finalResponse.ALL_RECORDS[0].UTR_NO) || 
                null;

  const isProcessed = transaction.status?.toLowerCase() === 'processed' || 
                      transaction.status?.toLowerCase() === 'completed' || 
                      transaction.status?.toLowerCase() === 'success';

  const handleRefreshStatus = async () => {
    if (!transaction.paymentRefNo) return;
    try {
      setRefreshing(true);
      await verifyPaymentStatus(transaction.paymentRefNo);
      toast.success("Transaction status refreshed successfully");
      if (onRefresh) {
        await onRefresh();
      }
    } catch (error: any) {
      console.error("Error refreshing transaction status:", error);
      toast.error(error?.response?.data?.message || "Failed to refresh transaction status");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#2e3b55', color: 'white', pb: 2 }}>
        Bank Transaction Details
      </DialogTitle>
      <DialogContent sx={{ mt: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1" fontWeight="bold">Status:</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip 
              label={transaction.status} 
              color={transaction.status?.toUpperCase().includes('INITIATED') ? 'warning' : transaction.status?.toUpperCase().includes('SUCCESS') ? 'success' : 'primary'}
              variant="outlined" 
              size="small" 
            />
            <IconButton 
              size="small" 
              onClick={handleRefreshStatus} 
              disabled={refreshing || isProcessed}
              title={isProcessed ? "Transaction is already processed" : "Refresh status"}
            >
              {refreshing ? <CircularProgress size={20} /> : <RefreshIcon />}
            </IconButton>
          </Box>
        </Box>
        
        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="textSecondary">Amount</Typography>
            <Typography variant="body1" fontWeight="500">₹{transaction.amount}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Transfer Type</Typography>
            <Typography variant="body1" fontWeight="500">{transaction.transferType}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Payment Ref No</Typography>
            <Typography variant="body1" fontWeight="500" sx={{ wordBreak: 'break-all' }}>{transaction.paymentRefNo || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Transaction ID</Typography>
            <Typography variant="body1" fontWeight="500" sx={{ wordBreak: 'break-all' }}>{transaction.transactionId || '-'}</Typography>
          </Box>
          
          <Box sx={{ gridColumn: 'span 2' }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Beneficiary Details</Typography>
          </Box>
          
          <Box sx={{ gridColumn: 'span 2' }}>
            <Typography variant="body2" color="textSecondary">Account Name</Typography>
            <Typography variant="body1" fontWeight="500">{transaction.beneAccName}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Account No</Typography>
            <Typography variant="body1" fontWeight="500">{transaction.beneAccNo}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">IFSC Code</Typography>
            <Typography variant="body1" fontWeight="500">{transaction.beneIfsc}</Typography>
          </Box>
          
          <Box sx={{ gridColumn: 'span 2' }}>
            <Divider sx={{ my: 1 }} />
          </Box>
          <Box>
            <Typography variant="body2" color="textSecondary">Date Created</Typography>
            <Typography variant="body1" fontWeight="500">{transaction.createdAt ? formatToDDMMYYYY(transaction.createdAt) : '-'}</Typography>
          </Box>
          {utrNo && (
            <Box>
              <Typography variant="body2" color="textSecondary">UTR Number</Typography>
              <Typography variant="body1" fontWeight="500" sx={{ wordBreak: 'break-all' }}>{utrNo}</Typography>
            </Box>
          )}
        </Box>
        
        {transaction.finalResponse && transaction.finalResponse.Transaction && (
          <Box sx={{ mt: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Bank Response</Typography>
            <Typography variant="body2" fontWeight="500">
              Message: {transaction.finalResponse.Transaction}
            </Typography>
            {transaction.finalResponse.CBX_API_REF_NO && (
              <Typography variant="body2" mt={0.5}>
                Bank Ref: {transaction.finalResponse.CBX_API_REF_NO}
              </Typography>
            )}
            {utrNo && (
              <Typography variant="body2" mt={0.5}>
                UTR Number: {utrNo}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailsModal;
